import { FormEvent, useState } from 'react';
import LoadingButton from '@mui/lab/LoadingButton';
import { Alert, Box, Dialog, DialogContent, DialogTitle, Typography } from '@mui/material';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { Appearance, loadStripe } from '@stripe/stripe-js';
import { useTranslation } from 'react-i18next';
import { theme } from '../../style/theme';
import { getConfig } from '../../utils/config';

const { stripePublishableKey } = getConfig();
const stripePromise = loadStripe(stripePublishableKey);

const appearance: Appearance = {
  theme: 'stripe',
  variables: {
    colorPrimary: theme.palette.primary.main,
    colorText: theme.palette.secondary.main,
  },
};

type PaymentModalProps = {
  open: boolean;
  onClose: () => void;
  returnUrl: string;
  clientSecret?: string;
};

export const PaymentModal = ({ returnUrl, clientSecret, open, onClose }: PaymentModalProps) => {
  const { t } = useTranslation('payment');
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{t('makeAPayment')}</DialogTitle>
      <DialogContent>
        {clientSecret ? (
          <Elements
            stripe={stripePromise}
            options={{
              clientSecret,
              appearance,
            }}
          >
            <PaymentCheckoutForm returnUrl={returnUrl} />
          </Elements>
        ) : (
          <Typography>{t('payment.generalError')}</Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};

type PaymentCheckoutFormProps = {
  returnUrl: string;
};

export function PaymentCheckoutForm({ returnUrl }: PaymentCheckoutFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useTranslation('payment');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      return;
    }

    setIsLoading(true);

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: returnUrl,
      },
    });

    // This point will only be reached if there is an immediate error when
    // confirming the payment. Otherwise, your customer will be redirected to
    // your `return_url`. For some payment methods like iDEAL, your customer will
    // be redirected to an intermediate site first to authorize the payment, then
    // redirected to the `return_url`.
    if (error.type === 'card_error' || error.type === 'validation_error') {
      setMessage(error.message ?? '');
    } else {
      setMessage(t('paymentError') as string);
    }

    setIsLoading(false);
  };

  return (
    <form id="payment-form" onSubmit={handleSubmit}>
      {/* Show any error or success messages */}
      {message && (
        <Box mb={2}>
          <Alert severity={'error'} id="payment-message">
            {message}
          </Alert>
        </Box>
      )}

      <PaymentElement id="payment-element" />

      <Box mt={4}>
        <LoadingButton
          id="submit"
          fullWidth={true}
          loading={isLoading}
          disabled={isLoading || !stripe || !elements}
          type={'submit'}
          variant={'contained'}
        >
          {t('pay')}
        </LoadingButton>
      </Box>
    </form>
  );
}
